import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { NotecardTextEditor } from 'swag-client-common/components/notecard-text-editor/notecard-text-editor';
import { NotecardEditorPanel } from 'swag-client-common/components/notecard-text-editor/notecard-editor-panel';
import { ContentState, convertToRaw, EditorState as EditorStateLibraryClass, Modifier, RichUtils } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { editorDefaultContentString, NOTECARD_EDITOR_STYLES_MAP } from 'swag-client-common/components/notecard-text-editor/constants';
import { MOCKUP_BOX_NOTECARD_HIDDEN_CONTAINER_ID, MOCKUP_HIDDEN_CONTAINER_STYLES } from 'swag-client-common/constants/html-to-image';
import { Buttons } from '../buttons';
import css from '../../edit-box-notecard-popup.scss';
const {
  FONT_SIZE_PREFIX
} = NOTECARD_EDITOR_STYLES_MAP;
export const TextEditorStepComponent = ({
  logo,
  color,
  textContent,
  feedbackText = '',
  isLoading,
  onNext,
  onBack,
  handleNotecardTextContentChange,
  onEditFeedback,
  isRevision
}) => {
  const [isInputValid, setIsInputValid] = useState(true);
  const [editorState, setEditorState] = useState(() => {
    if (textContent) {
      const blockFormHTML = htmlToDraft(textContent);
      const contentState = ContentState.createFromBlockArray(blockFormHTML.contentBlocks);
      return EditorStateLibraryClass.createWithContent(contentState);
    }
    const defaultContent = ContentState.createFromText(editorDefaultContentString);
    return EditorStateLibraryClass.createWithContent(defaultContent);
  });
  const editorRef = useRef(null);
  const editorWrapperRef = useRef(null);
  useEffect(() => {
    updateEditorRawContent();
  }, []);
  const selectionStylesFromState = useMemo(() => editorState.getCurrentInlineStyle().toJS(), [editorState]);
  const textAlignment = useMemo(() => {
    var _focusBlock$data$text, _focusBlock$data;
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const focusBlock = currentContent.getBlockForKey(selection.getFocusKey()).toJS();
    return (_focusBlock$data$text = (_focusBlock$data = focusBlock.data) === null || _focusBlock$data === void 0 ? void 0 : _focusBlock$data['text-align']) !== null && _focusBlock$data$text !== void 0 ? _focusBlock$data$text : null;
  }, [editorState]);
  const updateEditorRawContent = () => {
    const rawContent = convertToRaw(editorState.getCurrentContent());
    const html = draftToHtml(rawContent);
    handleNotecardTextContentChange(html);
  };
  const restoreEditorSelection = () => {
    const newState = EditorStateLibraryClass.forceSelection(editorState, editorState.getSelection());
    setEditorState(newState);
  };
  const handleSelectStyleChange = useCallback((inlineStyle, prefix) => {
    let stateForUpdate = editorState;
    if (prefix) {
      const existingStyles = stateForUpdate.getCurrentInlineStyle().toJS();
      const existingStyleWithPrefix = existingStyles.find(style => {
        return style.includes(prefix);
      });
      if (existingStyleWithPrefix) {
        stateForUpdate = RichUtils.toggleInlineStyle(stateForUpdate, existingStyleWithPrefix);
      }
    }
    const newStyle = prefix ? `${prefix}${inlineStyle}` : inlineStyle;
    stateForUpdate = RichUtils.toggleInlineStyle(stateForUpdate, newStyle);
    const activeSelection = stateForUpdate.getSelection();
    const selectionAnchorOffset = activeSelection.getAnchorOffset();
    const selectionAnchorFocusOffset = activeSelection.getFocusOffset();
    const isSelectionEmpty = selectionAnchorOffset === selectionAnchorFocusOffset;
    if (isSelectionEmpty && prefix !== FONT_SIZE_PREFIX) {
      return;
    }
    setEditorState(stateForUpdate);
    const rawContent = convertToRaw(stateForUpdate.getCurrentContent());
    handleNotecardTextContentChange(draftToHtml(rawContent));
  }, [editorState, setEditorState]);
  const changeEditorBlockDataBySelection = newBlockData => {
    const currentContent = editorState.getCurrentContent();
    const editorActiveSelection = editorState.getSelection();
    const modifiedContent = Modifier.mergeBlockData(currentContent, editorActiveSelection, newBlockData);
    const nextEditorState = EditorStateLibraryClass.push(editorState, modifiedContent, 'change-block-data');
    setEditorState(nextEditorState);
  };
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: css.title
  }, "Let\u2019s ", isRevision ? 'update' : 'design', " your notecard!"), /*#__PURE__*/React.createElement("div", {
    className: classnames(css.content, css.textEditorContent)
  }, /*#__PURE__*/React.createElement("div", {
    id: MOCKUP_BOX_NOTECARD_HIDDEN_CONTAINER_ID,
    style: MOCKUP_HIDDEN_CONTAINER_STYLES
  }), /*#__PURE__*/React.createElement(NotecardEditorPanel, {
    textAlignment: textAlignment,
    containerClassName: css.textEditorPanel,
    handleSelectStyleChange: handleSelectStyleChange,
    selectionStyles: selectionStylesFromState,
    changeEditorBlockDataBySelection: changeEditorBlockDataBySelection,
    restoreEditorSelection: restoreEditorSelection
  }), /*#__PURE__*/React.createElement("div", {
    className: css.textEditorWrapper
  }, /*#__PURE__*/React.createElement(NotecardTextEditor, {
    editorState: editorState,
    setEditorState: setEditorState,
    updateEditorRawContent: updateEditorRawContent,
    isInputValid: isInputValid,
    setIsInputValid: setIsInputValid,
    logo: logo,
    color: color,
    editorRef: editorRef,
    wrapperRef: editorWrapperRef,
    className: css.textEditor
  }), isRevision && /*#__PURE__*/React.createElement("div", {
    className: css.changesInputWrapper
  }, /*#__PURE__*/React.createElement("textarea", {
    placeholder: "You can make updates to your notecard text in the template above and provide any additional instructions here. Please note that any text entered here will not be reflected in your notecard.",
    className: css.changesInput,
    onChange: e => onEditFeedback && onEditFeedback(e.target.value),
    value: feedbackText,
    "data-input": "NotecardFeedback"
  })))), /*#__PURE__*/React.createElement(Buttons, {
    nextButtonDisabled: !isInputValid,
    nextButtonLoading: isLoading,
    nextText: isRevision ? 'Submit Revisions' : 'Done',
    backText: "Go Back",
    onNextButton: () => onNext(editorWrapperRef.current),
    onBackButton: onBack
  }));
};